import { Area, AreaChart, Cell, Pie, PieChart, ResponsiveContainer } from 'recharts';
import type { CollectionType, Modality } from '@/lib/contract';
import { collectionLabel, modalityLabel } from '@/lib/format';

/**
 * Dashboard charts.
 *
 * Colors come from the token set via CSS variables, never from literals, so the charts stay in
 * the same palette as the rest of the app. --color-info is reserved for chart series and is the
 * only place in the design where blue appears.
 */
const TOKEN: Record<string, string> = {
  info: 'var(--color-info)',
  pass: 'var(--color-pass)',
  warn: 'var(--color-warn)',
  danger: 'var(--color-danger)',
  muted: 'var(--color-muted)',
  primary: 'var(--color-primary)'
};

/**
 * Presentation mapping for chart series: which token each vocabulary value is drawn in.
 * This is styling, not data - the values themselves come from the API.
 */
const MODALITY_COLOR: Record<Modality, string> = {
  ego: 'info',
  teleop: 'pass',
  multi_rgb: 'warn',
  depth: 'muted',
  force_torque: 'danger',
  audio: 'muted',
  rgb_d: 'info'
};

const COLLECTION_COLOR: Record<CollectionType, string> = {
  teleoperation: 'info',
  human_demo: 'warn',
  scripted: 'pass',
  in_the_wild: 'muted'
};

/** Long-form modality names, as the dashboard mockup spells them. */
const MODALITY_LONG: Partial<Record<Modality, string>> = {
  ego: 'Egocentric video',
  teleop: 'Teleoperation',
  multi_rgb: 'Multi-view RGB',
  depth: 'Depth / LiDAR',
  force_torque: 'Tactile / F-T',
  audio: 'Audio'
};

export const toModalitySeries = (rows: { modality: Modality; hours: number }[]) =>
  rows.map(r => ({
    label: MODALITY_LONG[r.modality] ?? modalityLabel(r.modality),
    hours: r.hours,
    color: MODALITY_COLOR[r.modality] ?? 'muted'
  }));

export const toCollectionSeries = (rows: { type: CollectionType; share: number }[]) =>
  rows.map(r => ({
    label: collectionLabel(r.type),
    share: r.share,
    color: COLLECTION_COLOR[r.type] ?? 'muted'
  }));

/**
 * Horizontal bars. Plain divs rather than a chart library: the mockup is a label / bar / value
 * grid with fixed row heights, which CSS grid expresses better than a cartesian chart.
 */
export function HoursByModality({ data }: { data: { label: string; hours: number; color: string }[] }) {
  const max = Math.max(...data.map(d => d.hours), 1);
  return (
    <ul className="grid gap-3">
      {data.map(row => (
        <li key={row.label} className="grid grid-cols-[120px_1fr_54px] items-center gap-3">
          <span className="truncate text-[12px] text-muted">{row.label}</span>
          <span className="h-1.5 w-full overflow-hidden rounded-full bg-inset">
            <span
              className="block h-full rounded-full"
              style={{
                width: `${(row.hours / max) * 100}%`,
                backgroundColor: TOKEN[row.color] ?? TOKEN.muted
              }}
            />
          </span>
          <span className="text-right font-mono text-[11px] text-muted">{row.hours} h</span>
        </li>
      ))}
    </ul>
  );
}

/** Share-of-hours donut with an inline legend. */
export function CollectionDonut({ data }: { data: { label: string; share: number; color: string }[] }) {
  return (
    <div className="flex items-center gap-4">
      <div className="size-[104px] shrink-0">
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Pie
              data={data}
              dataKey="share"
              innerRadius={26}
              outerRadius={50}
              paddingAngle={2}
              stroke="none"
              isAnimationActive={false}
            >
              {data.map(entry => (
                <Cell key={entry.label} fill={TOKEN[entry.color] ?? TOKEN.muted} />
              ))}
            </Pie>
          </PieChart>
        </ResponsiveContainer>
      </div>
      <ul className="grid gap-2">
        {data.map(entry => (
          <li key={entry.label} className="flex items-center gap-2">
            <span className="size-1.5 rounded-full" style={{ backgroundColor: TOKEN[entry.color] ?? TOKEN.muted }} />
            <span className="text-[12px] text-muted">{entry.label}</span>
            <span className="ml-auto font-mono text-[11px] text-foreground">{entry.share}%</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

/** Upload activity sparkline: accent line over a faint accent wash, no axes. */
export function UploadSparkline({ data }: { data: { week: string; hours: number }[] }) {
  return (
    <div className="h-[104px] w-full">
      <ResponsiveContainer width="100%" height="100%">
        <AreaChart data={data} margin={{ top: 4, right: 2, bottom: 0, left: 2 }}>
          <defs>
            <linearGradient id="uploadFill" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="var(--color-primary)" stopOpacity={0.28} />
              <stop offset="100%" stopColor="var(--color-primary)" stopOpacity={0} />
            </linearGradient>
          </defs>
          <Area
            type="linear"
            dataKey="hours"
            stroke="var(--color-primary)"
            strokeWidth={1.5}
            fill="url(#uploadFill)"
            isAnimationActive={false}
            dot={false}
          />
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
}
